/**
 * Test-fit report section fallbacks (SNT-5).
 *
 * Pure deterministic fallbacks for the free-text fields on the
 * `test-fit` report-type schema (siteRag.ts:1735-1745). Each composer
 * reads only from `sed.siteProfile`, `sed.parcel`, `sed.flood` — no AI
 * calls, no I/O.
 *
 * Contract & composition mirror `feasibility.ts`. Recursion guard R6
 * pinned by `instructionShapeGuard.test.ts -> "fallback recursion guard"`.
 */
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { resolveWizardPreferencesForType } from '../coverage/resolveReportSetupFields';
import type { Recommendation, RiskItem, TestFitPreferences } from '../../types/siteRag';
import type { FindingClaim } from '../findingClaim';
import { formatFindingClaimDisplay } from '../findingClaim';

function testFitSetup(site: SiteEntity): TestFitPreferences | undefined {
  return resolveWizardPreferencesForType('test-fit', site);
}

/* ───────────────────────── helpers ──────────────────────────── */

function acresLabel(sed: SiteEvidenceDocument): string {
  const sp = sed.siteProfile?.data;
  if (sp && typeof sp.acres === 'number' && Number.isFinite(sp.acres) && sp.acres > 0) {
    return `${Math.round(sp.acres)} acres`;
  }
  const parcelAcres = sed.parcel?.data?.acres;
  if (typeof parcelAcres === 'number' && Number.isFinite(parcelAcres) && parcelAcres > 0) {
    return `${Math.round(parcelAcres)} acres`;
  }
  return '[—]';
}

function slopeLabel(sed: SiteEvidenceDocument): string {
  const sp = sed.siteProfile?.data;
  if (sp && typeof sp.slopePercent === 'number' && Number.isFinite(sp.slopePercent)) {
    return `${sp.slopePercent.toFixed(1)}%`;
  }
  return '[—]';
}

function elevationRangeLabel(sed: SiteEvidenceDocument): string {
  const sp = sed.siteProfile?.data;
  if (!sp) return '[—]';
  const min = sp.elevationMin;
  const max = sp.elevationMax;
  if (typeof min !== 'number' || typeof max !== 'number' || !Number.isFinite(min) || !Number.isFinite(max)) {
    return '[—]';
  }
  return `${Math.round(min)}–${Math.round(max)} m ASL`;
}

/**
 * Indicative buildable footprint — assumes a coarse 0.4 floor-area
 * ratio of usable acreage to an approximate square footage. We don't
 * compute that here; the cooked figure lives in the deterministic
 * scorer. The fallback only emits acres + a hedge so callers can pull
 * the structured number from PT3 / the scorer when present.
 */
function maxFootprintLabel(sed: SiteEvidenceDocument): string {
  return acresLabel(sed);
}

/* ───────────────────────── composers ────────────────────────── */

/**
 * Test-fit executive summary fallback.
 */
export function testFitExecutiveSummaryFallback(
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  parts.push(`${site.name} test-fit summary.`);
  const baseline: string[] = [];
  if (acresLabel(sed) !== '[—]') baseline.push(`area ${acresLabel(sed)}`);
  if (slopeLabel(sed) !== '[—]') baseline.push(`slope ${slopeLabel(sed)}`);
  if (elevationRangeLabel(sed) !== '[—]') baseline.push(`elevation ${elevationRangeLabel(sed)}`);
  if (baseline.length > 0) parts.push(`Site baseline: ${baseline.join('; ')}.`);
  parts.push('Detailed layout narrative pending re-run.');
  return parts.join(' ');
}

/**
 * Site constraints narrative fallback. Reads slope + flood zone +
 * elevation range.
 */
export function siteConstraintsFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  const s = slopeLabel(sed);
  if (s !== '[—]') parts.push(`slope ${s}`);
  const e = elevationRangeLabel(sed);
  if (e !== '[—]') parts.push(`elevation ${e}`);
  const fz = sed.flood?.data?.floodZone;
  if (typeof fz === 'string' && fz.length > 0) parts.push(`FEMA zone ${fz}`);
  if (parts.length === 0) return 'Site constraints narrative pending re-run.';
  return `Constraint indicators: ${parts.join('; ')}. Buildable-area narrative pending re-run.`;
}

/**
 * Building placement narrative fallback. Reads acreage + slope as
 * proxy footprint cues.
 */
export function buildingPlacementFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  const a = maxFootprintLabel(sed);
  if (a !== '[—]') parts.push(`gross area ${a}`);
  const s = slopeLabel(sed);
  if (s !== '[—]') parts.push(`slope ${s}`);
  if (parts.length === 0) return 'Building placement narrative pending re-run.';
  return `Placement baseline: ${parts.join('; ')}. Massing diagram pending re-run.`;
}

/**
 * Setback compliance narrative fallback. The SED carries zoning but
 * not the literal setback dimensions — render `[—]`.
 */
export function setbackComplianceFallback(
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const setup = testFitSetup(site);
  const pd = sed.parcel?.data;
  const parts: string[] = [];
  if (setup?.setbackOverrideFt != null && Number.isFinite(setup.setbackOverrideFt)) {
    parts.push(`user setback override ${setup.setbackOverrideFt} ft`);
  }
  if (pd?.zoning) parts.push(`zoning ${pd.zoning}`);
  if (parts.length === 0) parts.push('front/side/rear setback values: [—]');
  if (parts.length === 0) return 'Setback compliance narrative pending re-run.';
  return `Zoning anchor: ${parts.join('; ')}. Setback diagram pending re-run.`;
}

/**
 * Stormwater detention narrative fallback. Reads precipitation +
 * acreage.
 */
export function stormwaterDetentionFallback(
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const setup = testFitSetup(site);
  const cd = sed.climate?.data;
  const parts: string[] = [];
  if (setup?.stormwaterDetentionGal != null && Number.isFinite(setup.stormwaterDetentionGal)) {
    parts.push(`target detention ${setup.stormwaterDetentionGal.toLocaleString('en-US')} gal`);
  }
  if (cd && typeof cd.annualPrecipitation === 'number' && Number.isFinite(cd.annualPrecipitation)) {
    parts.push(`annual precipitation ${cd.annualPrecipitation.toFixed(0)} mm`);
  }
  if (acresLabel(sed) !== '[—]') parts.push(`parcel ${acresLabel(sed)}`);
  if (parts.length === 0) return 'Stormwater detention narrative pending re-run.';
  return `Hydrology indicators: ${parts.join('; ')}. Detention-pond sizing pending re-run.`;
}

/**
 * Utility routing narrative fallback. Reads on-site corridors when
 * present.
 */
export function utilityRoutingFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const inf = sed.onSiteInfrastructure?.data;
  const grid = sed.grid?.data;
  const parts: string[] = [];
  if (inf) {
    const fiber = inf.corridors?.find(c => c.kind === 'fiber');
    const tx = inf.corridors?.find(c => c.kind === 'transmission');
    const pipe = inf.corridors?.find(c => c.kind === 'pipeline');
    if (fiber && typeof fiber.distanceFromParcelKm === 'number' && Number.isFinite(fiber.distanceFromParcelKm)) {
      parts.push(`fiber ${fiber.distanceFromParcelKm.toFixed(2)} km from parcel`);
    }
    if (tx && typeof tx.distanceFromParcelKm === 'number' && Number.isFinite(tx.distanceFromParcelKm)) {
      parts.push(`transmission ${tx.distanceFromParcelKm.toFixed(2)} km from parcel`);
    }
    if (pipe && typeof pipe.distanceFromParcelKm === 'number' && Number.isFinite(pipe.distanceFromParcelKm)) {
      parts.push(`pipeline ${pipe.distanceFromParcelKm.toFixed(2)} km from parcel`);
    }
  }
  if (grid?.utilityName) parts.push(`utility ${grid.utilityName}`);
  if (grid?.substationName) parts.push(`substation ${grid.substationName}`);
  if (grid?.voltage && grid.voltage !== 'Unknown') parts.push(`interconnection voltage ${grid.voltage}`);
  if (parts.length === 0) return 'Utility routing narrative pending re-run.';
  return `KMZ corridor inventory: ${parts.join('; ')}. Route hierarchy pending re-run.`;
}

/**
 * Phasing narrative fallback. Surfaces acreage + slope as anchor.
 */
export function phaseLayoutFallback(
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const setup = testFitSetup(site);
  const a = acresLabel(sed);
  const s = slopeLabel(sed);
  const parts: string[] = [];
  if (setup?.targetFootprintAcres != null && Number.isFinite(setup.targetFootprintAcres)) {
    parts.push(`target footprint ${setup.targetFootprintAcres} ac`);
  }
  if (setup?.phasing === 'phased' && setup.phaseCount != null) {
    parts.push(`${setup.phaseCount}-phase buildout`);
  } else if (setup?.phasing === 'single') {
    parts.push('single-phase buildout');
  }
  if (a !== '[—]') parts.push(`gross area ${a}`);
  if (s !== '[—]') parts.push(`slope ${s}`);
  if (parts.length === 0) return 'Phasing narrative pending re-run.';
  return `Phasing baseline: ${parts.join('; ')}. Sequenced site-plan pending re-run.`;
}

/**
 * Test-fit conclusion recommendation fallback.
 */
export function testFitRecommendationFallback(
  _site: SiteEntity,
  _sed: SiteEvidenceDocument,
): string {
  return 'Test-fit recommendation pending re-run.';
}

/* ────────────── SNT-5 risk-register composer (QA 2026-06-10) ──────────────
 *
 * Holly Ridge blocker: test-fit reports shipped with `risks: []`,
 * `recommendations: []` and `conclusion.topRisks: []` while
 * `verdictLean.drivers` named four decision-grade risks and the
 * recommendation read "Proceed with caution" — a cautious verdict with a
 * structurally empty risk register. These composers derive the register
 * from the report's OWN already-persisted signals (verdictLean.drivers +
 * failed cross-validation checks) when — and only when — the model left
 * the fields empty. Pure report-data reads: no SED access, no I/O, no
 * Date/random; phrasing follows recursion guard R6 (declarative voice,
 * never the SNT-1 instruction-shape regex).
 */

/** One composed register entry — FindingClaim plus the typed-risk severity. */
export interface ComposedTestFitRisk {
  claim: FindingClaim;
  severity: 'high' | 'medium';
  origin: 'verdict-lean' | 'cross-validation';
}

/** Cap on composed register entries (drivers are ≤4 by schema). */
const RISK_REGISTER_MAX = 8;

function riskHeadline(text: string): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length <= 80 ? t : `${t.slice(0, 77).trimEnd()}…`;
}

/**
 * Compose FindingClaim risk entries from `verdictLean.drivers` (the bullets
 * that drove the verdict lean — `likely_present_unverified`, they cite
 * narrative evidence not re-verified here) and failed non-info
 * `crossValidationFindings` (`confirmed` — deterministic data checks).
 */
export function composeTestFitRiskRegister(
  data: Record<string, unknown>,
): ComposedTestFitRisk[] {
  const out: ComposedTestFitRisk[] = [];
  const seen = new Set<string>();
  const push = (entry: ComposedTestFitRisk): void => {
    const key = entry.claim.claim.replace(/\s+/g, ' ').trim().toLowerCase();
    if (key.length === 0 || seen.has(key)) return;
    seen.add(key);
    out.push(entry);
  };

  const verdictLean = data.verdictLean as { drivers?: unknown } | undefined;
  const drivers = Array.isArray(verdictLean?.drivers) ? verdictLean.drivers : [];
  for (const d of drivers) {
    if (typeof d !== 'string' || d.trim().length === 0) continue;
    push({
      claim: {
        claim: d.trim(),
        evidenceState: 'likely_present_unverified',
        suggestedFollowUp: 'Verification of this verdict driver against primary sources is pending re-run.',
      },
      severity: 'high',
      origin: 'verdict-lean',
    });
  }

  const cross = Array.isArray(data.crossValidationFindings) ? data.crossValidationFindings : [];
  for (const raw of cross) {
    if (raw == null || typeof raw !== 'object') continue;
    const c = raw as { rule?: unknown; passed?: unknown; message?: unknown; severity?: unknown };
    if (typeof c.message !== 'string' || c.message.trim().length === 0) continue;
    // Assembly already filters to failed non-info checks; re-filter defensively.
    if (c.passed === true || c.severity === 'info') continue;
    push({
      claim: {
        claim: c.message.trim(),
        evidenceState: 'confirmed',
        evidence: {
          refIds: [],
          excerpt: `Deterministic cross-validation rule: ${typeof c.rule === 'string' && c.rule ? c.rule : 'unnamed check'}`,
        },
      },
      severity: c.severity === 'error' ? 'high' : 'medium',
      origin: 'cross-validation',
    });
  }

  return out.slice(0, RISK_REGISTER_MAX);
}

function isMissingOrEmptyArray(value: unknown): boolean {
  return value == null || (Array.isArray(value) && value.length === 0);
}

/**
 * Fill `conclusion.topRisks` + top-level `risks` / `recommendations` on a
 * test-fit report record when the model left them empty and the register is
 * composable (non-empty drivers / failed cross-validation findings). Mutates
 * `data` in place (callers pass the applySectionFallbacks clone) and returns
 * the field paths filled so the caller can stamp `sectionGuardStatus`.
 * Non-empty fields are NEVER clobbered.
 */
export function applyTestFitRiskRegisterFallback(
  data: Record<string, unknown>,
): string[] {
  const composed = composeTestFitRiskRegister(data);
  if (composed.length === 0) return [];
  const appliedPaths: string[] = [];

  // conclusion.topRisks — the persisted/display contract is string[]
  // (normalizeStoredConclusion drops non-string entries), so the FindingClaim
  // display projection is written. The epistemic state survives as the
  // "(verification pending)" suffix from formatFindingClaimDisplay.
  const conclusion = data.conclusion;
  if (conclusion != null && typeof conclusion === 'object' && !Array.isArray(conclusion)) {
    const c = conclusion as Record<string, unknown>;
    if (isMissingOrEmptyArray(c.topRisks)) {
      c.topRisks = composed.slice(0, 4).map((e) => formatFindingClaimDisplay(e.claim));
      appliedPaths.push('conclusion.topRisks');
    }
  }

  // Top-level risks (RiskItem[]) — the source the viewer's RiskMatrixSection
  // and `deriveRiskMatrix` read.
  if (isMissingOrEmptyArray(data.risks)) {
    data.risks = composed.map((e, i): RiskItem => ({
      id: `tf-risk-register-${i + 1}`,
      category: 'execution',
      label: riskHeadline(e.claim.claim),
      severity: e.severity,
      impact: e.claim.claim,
      mitigation: 'Mitigation planning is pending re-run.',
    }));
    appliedPaths.push('risks');
  }

  // Top-level recommendations (Recommendation[]).
  if (isMissingOrEmptyArray(data.recommendations)) {
    data.recommendations = composed.slice(0, 4).map((e): Recommendation => ({
      type: 'site',
      title: `Risk focus: ${riskHeadline(e.claim.claim)}`,
      description: e.claim.claim,
      rationale: e.origin === 'verdict-lean'
        ? 'Composed from the report verdict-lean drivers (SNT-5 risk-register fallback); detailed action plan pending re-run.'
        : 'Composed from failed deterministic cross-validation checks (SNT-5 risk-register fallback); detailed action plan pending re-run.',
    }));
    appliedPaths.push('recommendations');
  }

  return appliedPaths;
}

/**
 * RTU-3 (row 0.99235) — constructability assessment fallback (AGC of
 * America Constructability Review + Mortenson / Turner practice).
 *
 * The SED today does not carry an "equipment access / crane lay-down"
 * inventory — we surface the dominant constructability driver(s) the
 * SED actually flags (slope, parcel area, flood zone, fragmented
 * water-feature constraints) so a reviewer can see what the
 * constructability review would weight against, and defer the full
 * assessment to the re-run.
 *
 * Phrasing discipline (recursion guard R6): past/declarative voice,
 * no "should cite" / "must address" / "reference X" verbs.
 */
export function constructabilityAssessmentFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  const a = acresLabel(sed);
  const s = slopeLabel(sed);
  const indicators: string[] = [];
  if (a !== '[—]') indicators.push(`gross area ${a}`);
  if (s !== '[—]') indicators.push(`slope ${s}`);
  const fz = sed.flood?.data?.floodZone;
  if (typeof fz === 'string' && fz.length > 0 && fz !== 'X') {
    indicators.push(`FEMA zone ${fz}`);
  }
  const wf = sed.onSiteInfrastructure?.data?.waterFeatures;
  if (Array.isArray(wf) && wf.length > 0) {
    indicators.push(`${wf.length} KMZ-tagged water feature(s) near parcel`);
  }
  if (indicators.length > 0) {
    parts.push(`Constructability baseline: ${indicators.join('; ')}.`);
  } else {
    parts.push('Constructability baseline: [—].');
  }
  parts.push('Equipment access, lay-down and crane-staging detail pending re-run.');
  return parts.join(' ');
}

/**
 * RTU-3 — cost range estimate fallback (RSMeans Heavy / Building +
 * Mortenson / Turner programme benchmarks).
 *
 * The SED carries no per-line-item cost data — we surface acreage as
 * the dominant cost driver and explicitly mark the RSMeans line
 * categories the eventual estimate will compose, with the per-MW total
 * deferred to the re-run.
 *
 * Phrasing discipline (recursion guard R6): past/declarative voice,
 * never the SNT-1 instruction-shape regex.
 */
export function costRangeEstimateFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  const a = acresLabel(sed);
  if (a !== '[—]') {
    parts.push(`Cost-driver baseline: ${a} gross.`);
  } else {
    parts.push('Cost-driver baseline: [—].');
  }
  parts.push(
    'RSMeans line categories: mass grading (Heavy 31 22 13); foundations + shell '
    + '(Building 03 30 00 + 05 12 23); MEP rough-in (Building 23 / 26 / 27); '
    + 'site utilities (Heavy 33 / 32).',
  );
  parts.push('Per-MW / per-SF range pending re-run.');
  return parts.join(' ');
}
